import { createFileRoute, Outlet, redirect, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { meApi } from "@/api/me";
import { authApi } from "@/api/auth";
import { useAuthStore, isAuthenticated } from "@/auth/store";
import { ShieldCheck, FolderKanban, Users, Receipt, FileSearch, Ban, ArrowLeft, LogOut, Coins, Layers, Ticket } from "lucide-react";

export const Route = createFileRoute("/admin")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: AdminShell,
});

function AdminShell() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);

  const { isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const me = await meApi.get();
      setUser(me.user);
      return me;
    },
  });

  // After /me lands, redirect non-admins out.
  if (!isLoading && user && user.role !== "admin") {
    throw redirect({ to: "/app" });
  }

  async function logout() {
    try { await authApi.logout(); } catch { /* ignore */ }
    useAuthStore.getState().clear();
    navigate({ to: "/login", replace: true });
  }

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-60 shrink-0 border-r border-ink-50/10 bg-ink-500 px-4 py-6 text-ink-50 md:flex md:flex-col">
        <div className="flex items-center gap-2 px-2 text-lg font-semibold text-ink-50">
          <ShieldCheck size={18} /> Admin
        </div>
        <nav className="mt-8 flex flex-col gap-0.5 text-sm">
          <NavItem to="/admin"           icon={<FolderKanban size={16} />}>Projects</NavItem>
          <NavItem to="/admin/users"     icon={<Users size={16} />}>Users</NavItem>
          <NavItem to="/admin/plans"     icon={<Layers size={16} />}>Plans</NavItem>
          <NavItem to="/admin/promos"    icon={<Ticket size={16} />}>Promo codes</NavItem>
          <NavItem to="/admin/payments"  icon={<Receipt size={16} />}>Payments</NavItem>
          <NavItem to="/admin/sweeps"    icon={<Coins size={16} />}>Onchain sweeps</NavItem>
          <NavItem to="/admin/ip-blocks" icon={<Ban size={16} />}>IP denylist</NavItem>
          <NavItem to="/admin/audit"     icon={<FileSearch size={16} />}>Audit log</NavItem>
        </nav>
        <div className="mt-auto border-t border-ink-50/10 pt-6">
          <Link to="/app" className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink-50/65 transition hover:bg-ink-50/10 hover:text-ink-50">
            <ArrowLeft size={16} /> Back to app
          </Link>
          <button onClick={logout} className="mt-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-ink-50/65 transition hover:bg-ink-50/10 hover:text-ink-50">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 px-6 py-10 md:px-10">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({ to, icon, children }: { to: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      activeOptions={{ exact: true }}
      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-ink-50/65 transition hover:bg-ink-50/10 hover:text-ink-50 [&.active]:bg-ink-50/15 [&.active]:text-ink-50"
    >
      {icon} {children}
    </Link>
  );
}
